import { useEffect, useRef, useCallback } from 'react';
import socketIOClient from 'socket.io-client';
import {
  Message,
  MessageType,
  SendMessagePayload,
  SocketErrorResponse,
  WebSocketEvent,
  TypingIndicatorPayload,
  MessageQueryParams,
} from './messaging.interfaces';
import { MessagingAPI } from './messaging.api';
import { localStorageGet } from '../../utils/localStorage';
import {
  mockMessages,
  mockConversationListResponse,
} from '../../__mocks__/messaging/messageResponse.mock';
import isAppWithMock from 'src/utils/isAppWithMock';

// Type for socket instance  
type SocketInstance = SocketIOClient.Socket;

export const useMessagingService = () => {
  const socketRef = useRef<SocketInstance | null>(null);
  const isConnectedRef = useRef<boolean>(false);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  
  const withMockData = isAppWithMock();
  const socketUrl = `${process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_BASE_URL || 'http://localhost:3001'}/messages`;

  // Initialize socket connection with JWT authentication
  useEffect(() => {
    if (withMockData) {
      // In mock mode, simulate connection success
      isConnectedRef.current = true;
      return;
    }

    if (!socketRef.current) {
      const token = localStorageGet('token');
      
      if (!token) {
        console.warn('No authentication token found for WebSocket connection');
        return;
      }

      const socket = socketIOClient(socketUrl, {
        auth: {
          token: token,
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: 1000,
      });
      
      socketRef.current = socket;

      socket.on(WebSocketEvent.CONNECT, () => {
        console.log('✅ Connected to messaging server');
        isConnectedRef.current = true;
        reconnectAttemptsRef.current = 0;
      });

      socket.on(WebSocketEvent.DISCONNECT, (reason: string) => {
        console.log('❌ Disconnected from messaging server:', reason);
        isConnectedRef.current = false;
      });

      socket.on(WebSocketEvent.CONNECT_ERROR, (error: Error) => {
        console.error('Connection error:', error.message);
        reconnectAttemptsRef.current += 1;
        
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error('Max reconnection attempts reached. Falling back to REST API.');
        }
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        isConnectedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get all messages (direct messages and broadcasts) - Use REST API
  const getAllMessages = useCallback(
    async (
      params?: MessageQueryParams
    ): Promise<{ data: Message[]; meta?: { total: number; page: number; limit: number } }> => {
      if (withMockData) {
        let filtered = [...mockMessages];
        if (params?.type) {
          filtered = filtered.filter((m) => m.type === params.type);
        }
        if (params?.receiver_id) {
          filtered = filtered.filter(
            (m) => m.receiver_id === params.receiver_id || m.sender_id === params.receiver_id
          );
        }
        if (params?.class_id) {
          filtered = filtered.filter((m) => m.class_id === params.class_id);
        }
        const page = params?.page ?? 1;
        const limit = params?.limit ?? 20;
        const start = (page - 1) * limit;
        const paginated = filtered.slice(start, start + limit);
        return {
          data: paginated,
          meta: { total: filtered.length, page, limit },
        };
      }

      return Promise.resolve({
        data: [],
        meta: { total: 0, page: params?.page || 1, limit: params?.limit || 50 },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Get direct messages with a specific user - Use REST API
  const getDirectMessages = useCallback(
    async (
      receiver_id: string,
      params?: { page?: number; limit?: number }
    ): Promise<{ data: Message[]; meta?: { total: number; page: number; limit: number } }> => {
      if (withMockData) {
        const filtered = mockMessages.filter(
          (m) =>
            m.type === MessageType.DIRECT_MESSAGE &&
            (m.receiver_id === receiver_id || m.sender_id === receiver_id)
        );
        const page = params?.page ?? 1;
        const limit = params?.limit ?? 20;
        const start = (page - 1) * limit;
        const paginated = filtered.slice(start, start + limit);
        return {
          data: paginated,
          meta: { total: filtered.length, page, limit },
        };
      }

      try {
        const response = await MessagingAPI.getConversationMessages(
          receiver_id,
          params?.page,
          params?.limit
        );
        
        return {
          data: response.messages,
          meta: {
            total: response.total,
            page: response.page,
            limit: response.limit,
          },
        };
      } catch (error) {
        console.error('Failed to fetch direct messages:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Get broadcast messages - Use REST API
  const getBroadcastMessages = useCallback(
    async (
      class_id?: string,
      params?: { page?: number; limit?: number; instituteId?: string }
    ): Promise<{ data: Message[]; meta?: { total: number; page: number; limit: number } }> => {
      if (withMockData) {
        let filtered = mockMessages.filter((m) => m.type === MessageType.BROADCAST);
        if (class_id) {
          filtered = filtered.filter((m) => m.class_id === class_id);
        }
        const page = params?.page ?? 1;
        const limit = params?.limit ?? 20;
        const start = (page - 1) * limit;
        const paginated = filtered.slice(start, start + limit);
        return {
          data: paginated,
          meta: { total: filtered.length, page, limit },
        };
      }

      try {
        if (!params?.instituteId) {
          console.warn('Institute ID required for fetching broadcasts');
          return { data: [], meta: { total: 0, page: 1, limit: 50 } };
        }

        const response = await MessagingAPI.getBroadcasts(
          params.instituteId,
          class_id,
          params?.page,
          params?.limit
        );
        
        return {
          data: response.messages,
          meta: {
            total: response.total,
            page: response.page,
            limit: response.limit,
          },
        };
      } catch (error) {
        console.error('Failed to fetch broadcast messages:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Get conversations list - Use REST API
  const getConversations = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
    }): Promise<{ data: any[]; meta?: { total: number; page: number; limit: number } }> => {
      if (withMockData) {
        const page = params?.page ?? 1;
        const limit = params?.limit ?? 20;
        const start = (page - 1) * limit;
        const paginated = mockConversationListResponse.data.slice(start, start + limit);
        return {
          data: paginated,
          meta: {
            total: mockConversationListResponse.meta?.total ?? mockConversationListResponse.data.length,
            page,
            limit,
          },
        };
      }

      try {
        const conversations = await MessagingAPI.getConversations();
        
        return {
          data: conversations,
          meta: {
            total: conversations.length,
            page: 1,
            limit: 50,
          },
        };
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Send a direct message - Try WebSocket first, fallback to REST API
  const sendDirectMessage = useCallback(
    async (
      payload: SendMessagePayload & { sender_id?: string }
    ): Promise<Message> => {
      if (withMockData) {
        const msgId = `m${Date.now()}`;
        const newMessage: Message = {
          id: msgId,
          _id: msgId,
          content: payload.content,
          sender_id: payload.sender_id || 'mock_user',
          receiver_id: payload.receiver_id,
          type: MessageType.DIRECT_MESSAGE,
          institute_ref: payload.institute_ref,
          is_read: false,
          created_at: new Date().toISOString(),
        };
        return Promise.resolve(newMessage);
      }

      // Try WebSocket first if connected
      if (socketRef.current && isConnectedRef.current) {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            socketRef.current?.off('messageSent', onSent);
            console.warn('WebSocket send timeout, falling back to REST API');
            MessagingAPI.sendMessage(payload)
              .then((response) => resolve(response.message || response as any))
              .catch(reject);
          }, 5000);

          const onSent = (message: Message) => {
            clearTimeout(timeout);
            resolve(message);
          };

          // Listen once for the messageSent confirmation
          socketRef.current!.once('messageSent', onSent);

          // Backend expects created_by instead of sender_id
          const wsPayload = {
            ...payload,
            created_by: payload.sender_id,
          };
          socketRef.current!.emit(WebSocketEvent.SEND_MESSAGE, wsPayload);
        });
      }

      // Fallback to REST API if WebSocket not available
      try {
        const response = await MessagingAPI.sendMessage(payload);
        return response.message || response as any;
      } catch (error) {
        console.error('Failed to send message via REST API:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Send a broadcast message
  const sendBroadcastMessage = useCallback(
    async (
      payload: any & { sender_id?: string }
    ): Promise<Message> => {
      if (withMockData) {
        const msgId = `m${Date.now()}`;
        const newMessage: Message = {
          id: msgId,
          _id: msgId,
          content: payload.content,
          sender_id: payload.sender_id || 'mock_user',
          class_id: payload.class_id,
          section_id: payload.section_id,
          type: MessageType.BROADCAST,
          institute_ref: payload.institute_ref,
          is_read: false,
          created_at: new Date().toISOString(),
        };
        return Promise.resolve(newMessage);
      }

      // Broadcasts are sent via REST API according to documentation
      try {
        const broadcastPayload = {
          broadcastMessage: {
            content: payload.content,
            institute_ref: payload.institute_ref,
            class_id: payload.class_id,
            section_id: payload.section_id,
          },
          target: {
            scope: payload.scope || 'institute',
            institute_id: payload.institute_ref,
            class_id: payload.class_id,
            section: payload.section_id,
          },
        };

        await MessagingAPI.sendBroadcast(broadcastPayload);
        
        // Return a placeholder message since broadcast API doesn't return the message
        const message: Message = {
          id: `broadcast-${Date.now()}`,
          content: payload.content,
          sender_id: payload.sender_id || 'current-user',
          type: 'broadcast' as any,
          institute_ref: payload.institute_ref,
          class_id: payload.class_id,
          section_id: payload.section_id,
          is_read: true,
          created_at: new Date().toISOString(),
        };
        
        return message;
      } catch (error) {
        console.error('Failed to send broadcast:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Listen for new messages (real-time via WebSocket)
  const onNewMessage = useCallback(
    (callback: (message: Message) => void) => {
      if (withMockData) {
        // In mock mode, return a cleanup function that does nothing
        return () => {};
      }

      if (!socketRef.current) return () => {};

      socketRef.current.on(WebSocketEvent.MESSAGE, callback);

      return () => {
        if (socketRef.current) {
          socketRef.current.off(WebSocketEvent.MESSAGE, callback);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Listen for broadcast messages (real-time via WebSocket)
  const onBroadcast = useCallback(
    (callback: (message: Message) => void) => {
      if (withMockData) {
        return () => {};
      }

      if (!socketRef.current) return () => {};

      socketRef.current.on(WebSocketEvent.BROADCAST, callback);

      return () => {
        if (socketRef.current) {
          socketRef.current.off(WebSocketEvent.BROADCAST, callback);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Listen for message sent confirmation (not in API docs but keeping for compatibility)
  const onMessageSent = useCallback(
    (callback: (message: Message) => void) => {
      if (withMockData) {
        return () => {};
      }

      if (!socketRef.current) return () => {};

      // This event isn't in the official docs, but keeping for backward compatibility
      socketRef.current.on('messageSent', callback);

      return () => {
        if (socketRef.current) {
          socketRef.current.off('messageSent', callback);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Listen for group messages (real-time via WebSocket)
  const onGroupMessage = useCallback(
    (callback: (data: { groupId: string; message: Message }) => void) => {
      if (withMockData) {
        return () => {};
      }

      if (!socketRef.current) return () => {};

      socketRef.current.on(WebSocketEvent.GROUP_MESSAGE, callback);

      return () => {
        if (socketRef.current) {
          socketRef.current.off(WebSocketEvent.GROUP_MESSAGE, callback);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Listen for errors
  const onError = useCallback(
    (callback: (error: SocketErrorResponse) => void) => {
      if (withMockData) {
        return () => {};
      }

      if (!socketRef.current) return () => {};

      socketRef.current.on(WebSocketEvent.ERROR, callback);

      return () => {
        if (socketRef.current) {
          socketRef.current.off(WebSocketEvent.ERROR, callback);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Mark messages as read - Use REST API
  const markMessagesAsRead = useCallback(
    async (
      message_ids: string[]
    ): Promise<{ success: boolean; updated_count: number }> => {
      if (withMockData) {
        return Promise.resolve({
          success: true,
          updated_count: message_ids.length,
        });
      }

      try {
        // Mark each message individually as per API docs
        await Promise.all(
          message_ids.map((id) => MessagingAPI.markMessageAsRead(id))
        );

        return {
          success: true,
          updated_count: message_ids.length,
        };
      } catch (error) {
        console.error('Failed to mark messages as read:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Mark entire conversation as read - Use REST API
  const markConversationAsRead = useCallback(
    async (otherUserId: string): Promise<void> => {
      if (withMockData) {
        return Promise.resolve();
      }

      try {
        await MessagingAPI.markConversationAsRead(otherUserId);
      } catch (error) {
        console.error('Failed to mark conversation as read:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Get unread count - Use REST API
  const getUnreadCount = useCallback(
    async (): Promise<number> => {
      if (withMockData) {
        return Promise.resolve(0);
      }

      try {
        return await MessagingAPI.getUnreadCount();
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Deprecated/Legacy event listeners for backward compatibility
  const onTyping = useCallback((callback: (indicator: TypingIndicatorPayload) => void) => {
    console.warn('Typing indicators not yet implemented');
    return () => {};
  }, []);
  const onStopTyping = useCallback((callback: (indicator: TypingIndicatorPayload) => void) => {
    console.warn('Typing indicators not yet implemented');
    return () => {};
  }, []);
  const sendTypingIndicator = useCallback((payload: TypingIndicatorPayload) => {
    console.warn('Typing indicators not yet implemented');
  }, []);
  const sendStopTypingIndicator = useCallback((payload: TypingIndicatorPayload) => {
    console.warn('Typing indicators not yet implemented');
  }, []);
  const onConnectionSuccess = useCallback(() => () => {}, []);

  // Check connection status
  const isConnected = useCallback(() => {
    return withMockData ? true : isConnectedRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // Connection
    isConnected,
    onError,
    onConnectionSuccess, // Legacy

    // Messages (REST API)
    getAllMessages,
    getDirectMessages,
    getBroadcastMessages,
    getConversations,
    
    // Sending messages (WebSocket with REST fallback)
    sendDirectMessage,
    sendBroadcastMessage,
    
    // Real-time listeners (WebSocket)
    onNewMessage,
    onBroadcast,
    onGroupMessage,
    onMessageSent, // Legacy
    
    // Mark as read (REST API)
    markMessagesAsRead,
    markConversationAsRead,
    getUnreadCount,

    // Legacy/deprecated - kept for backward compatibility
    onTyping,
    onStopTyping,
    sendTypingIndicator,
    sendStopTypingIndicator,
  };
};
